/* AtmoSvetla theme — no dependencies. Every component works without JS (content stays visible). */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal on scroll */
  const initReveal = (root = document) => {
    const items = root.querySelectorAll('[data-reveal]:not(.is-in)');
    if (!items.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    items.forEach((el) => io.observe(el));
  };

  /* Hero: quick orientation — switches price card and background photo */
  class HeroOrientation extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-type-index]')];
      this.images = [...this.querySelectorAll('[data-bg-index]')];
      this.buttons.forEach((btn) => btn.addEventListener('click', () => this.select(Number(btn.dataset.typeIndex))));
    }
    select(index) {
      const btn = this.buttons[index];
      if (!btn) return;
      this.buttons.forEach((b, i) => b.setAttribute('aria-pressed', String(i === index)));
      this.images.forEach((img, i) => img.classList.toggle('is-active', i === index));
      const set = (name, value) => {
        const el = this.querySelector(`[data-quick="${name}"]`);
        if (el) el.textContent = value || '';
      };
      set('price', btn.dataset.price);
      set('time', btn.dataset.time);
      set('note', btn.dataset.note);
      set('caption', btn.dataset.caption);
      const caption = this.querySelector('[data-quick-caption]');
      if (caption) caption.hidden = !btn.dataset.caption;
      document.dispatchEvent(new CustomEvent('atmo:type-selected', { detail: { value: btn.dataset.value } }));
    }
  }
  customElements.define('hero-orientation', HeroOrientation);


  /* Neon studio — live preview of the customer's own text */
  class NeonStudio extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-studio-text]');
      this.input = this.querySelector('[data-studio-input]');
      this.power = this.querySelector('[data-studio-power]');
      this.colorName = this.querySelector('[data-studio-color-name]');
      this.swatches = [...this.querySelectorAll('[data-studio-swatch]')];
      this.fallback = this.text ? this.text.textContent : '';
      this.input?.addEventListener('input', () => {
        const v = this.input.value.trim();
        this.text.textContent = v || this.fallback;
      });
      this.swatches.forEach((sw) => sw.addEventListener('click', () => {
        this.swatches.forEach((b) => b.setAttribute('aria-pressed', String(b === sw)));
        this.text.style.setProperty('--tube', sw.dataset.hex);
        if (this.colorName) this.colorName.textContent = sw.dataset.name;
      }));
      this.power?.addEventListener('change', () => this.text.classList.toggle('is-off', !this.power.checked));
      this.querySelector('[data-studio-send]')?.addEventListener('click', () => {
        const color = this.colorName ? this.colorName.textContent : '';
        document.dispatchEvent(new CustomEvent('atmo:type-selected', { detail: { value: 'LED neon' } }));
        document.dispatchEvent(new CustomEvent('atmo:prefill', { detail: { text: `Text: „${this.text.textContent}“, barva: ${color}` } }));
      });
    }
  }
  customElements.define('neon-studio', NeonStudio);


  /* Product form — variant pills update price, availability and URL */
  const formatMoney = (cents, format) => {
    const n = (cents / 100);
    const fix = (v, d) => v.toFixed(d);
    const withSep = (v, d, thousands, dec) => {
      const [i, f] = fix(v, d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (f ? dec + f : '');
    };
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withSep(n, 0, ',', '.');
      if (key === 'amount_with_comma_separator') return withSep(n, 2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return withSep(n, 0, '.', ',');
      if (key === 'amount_with_space_separator') return withSep(n, 2, ' ', ',');
      if (key === 'amount_no_decimals_with_space_separator') return withSep(n, 0, ' ', ',');
      return withSep(n, 2, ',', '.');
    });
  };
  class ProductForm extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.fieldsets = [...this.querySelectorAll('[data-option-index]')];
      this.addEventListener('change', (e) => { if (e.target.matches('[data-option-index] input')) this.update(); });
    }
    update() {
      const selected = this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
      this.fieldsets.forEach((fs, i) => { const out = fs.querySelector('[data-option-value]'); if (out) out.textContent = selected[i]; });
      const v = this.variants.find((x) => x.options.every((o, i) => o === selected[i]));
      const btn = this.querySelector('[data-add]');
      const label = this.querySelector('[data-add-label]');
      if (!v) { btn.disabled = true; label.textContent = this.dataset.labelSold; return; }
      this.querySelector('[data-variant-id]').value = v.id;
      this.querySelector('[data-price]').textContent = formatMoney(v.price, this.dataset.moneyFormat);
      const cmp = this.querySelector('[data-compare]');
      if (cmp) { cmp.hidden = !(v.compare_at_price > v.price); cmp.textContent = formatMoney(v.compare_at_price || 0, this.dataset.moneyFormat); }
      btn.disabled = !v.available;
      label.textContent = v.available ? this.dataset.labelAdd : this.dataset.labelSold;
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url);
    }
  }
  customElements.define('product-form', ProductForm);

  /* Tabs (industries) — arrow keys supported */
  class TabsBlock extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.show(i));
        tab.addEventListener('keydown', (e) => {
          const vertical = window.matchMedia('(min-width: 990px)').matches;
          const next = vertical ? 'ArrowDown' : 'ArrowRight';
          const prev = vertical ? 'ArrowUp' : 'ArrowLeft';
          if (e.key === next || e.key === prev) {
            e.preventDefault();
            const to = (i + (e.key === next ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.show(to);
            this.tabs[to].focus();
          }
        });
      });
    }
    show(index) {
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', String(i === index));
        t.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((p, i) => { p.hidden = i !== index; });
    }
  }
  customElements.define('tabs-block', TabsBlock);

  /* Lead form: steps + preselected type from the hero card */
  class LeadForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.steps = [...this.querySelectorAll('fieldset[data-step]')];
      this.progress = [...this.querySelectorAll('[data-progress]')];
      this.current = 0;
      if (this.steps.length > 1) {
        this.form.setAttribute('data-steps', '');
        this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => this.go(this.current + 1)));
        this.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => this.go(this.current - 1)));
        this.go(this.form.querySelector('.form-message--error') ? this.steps.length - 1 : 0);
      }
      document.addEventListener('atmo:type-selected', (e) => this.preselect(e.detail.value));
      document.addEventListener('atmo:prefill', (e) => {
        const body = this.querySelector('textarea[name="contact[body]"]');
        if (body && !body.value.includes(e.detail.text)) body.value = (e.detail.text + '\n' + body.value).trim();
      });
      const fromUrl = new URLSearchParams(location.search).get('typ');
      if (fromUrl) this.preselect(fromUrl);
      this.initFiles();
    }
    /* Files: up to 4, uploaded to Google Drive (Apps Script) on step 2, before the contact step.
       The Shopify contact form itself is submitted normally (its CAPTCHA stays intact). */
    initFiles() {
      this.box = this.querySelector('[data-upload]');
      if (!this.box || !this.form) return;
      this.files = [];
      this.done = [];
      this.newCode();
      this.msg = this.box.dataset;
      this.input = this.box.querySelector('[data-files]');
      this.list = this.box.querySelector('[data-file-list]');
      this.error = this.box.querySelector('[data-file-error]');
      this.note = this.box.querySelector('[data-files-note]');
      this.progressBox = this.box.querySelector('[data-progress-box]');
      this.bar = this.box.querySelector('[data-bar]');
      this.status = this.box.querySelector('[data-status]');
      this.nextBtn = this.querySelector('[data-files-next]');
      this.withoutBtn = this.box.querySelector('[data-continue-without]');
      this.input.addEventListener('change', () => { this.addFiles(this.input.files); this.input.value = ''; });
      const drop = this.box.querySelector('[data-drop]');
      ['dragenter', 'dragover'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, () => drop.classList.remove('is-over')));
      drop.addEventListener('drop', (e) => { e.preventDefault(); if (!this.busy) this.addFiles(e.dataTransfer.files); });
      this.nextBtn.addEventListener('click', () => this.filesNext());
      this.box.querySelector('[data-cancel]').addEventListener('click', () => this.cancelUpload());
      // contact step sent: give the Drive folder the customer's name and the whole request (fire-and-forget,
      // the Shopify form itself is submitted normally so its CAPTCHA keeps working)
      this.form.addEventListener('submit', () => {
        if (!this.done.length) return;
        const val = (n) => (this.form.querySelector(`[name="${n}"]`) || {}).value || '';
        const typ = this.form.querySelector('input[name="contact[Typ]"]:checked');
        const body = ['Poptávka z webu – kód ' + this.code + ' (stránka ' + location.pathname + ')', 'Typ: ' + (typ ? typ.value : '—'), '', val('contact[body]')].join('\n');
        try {
          fetch(this.box.dataset.upload, {
            method: 'POST', keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({ update: true, code: this.code, name: val('contact[name]').trim(), email: val('contact[email]'), phone: val('contact[phone]'), body })
          }).catch(() => {});
        } catch (e) { /* the request still goes to the shop e-mail */ }
      });
      this.withoutBtn.addEventListener('click', () => {
        this.setNote('Zákazník chtěl poslat soubory, nahrání selhalo: ' + this.files.map((f) => f.name).join(', ') + ' — vyžádat e-mailem.');
        this.files = [];
        this.showError('');
        this.renderFiles();
        this.go(this.current + 1);
      });
      this.renderFiles();
    }
    newCode() {
      this.code = Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[b % 32]).join('');
    }
    // stop a running upload (wrong or too big file); the files stay in the list so one can be removed
    cancelUpload() {
      if (!this.busy || !this.abort) return;
      this.abort.abort();
    }
    addFiles(fileList) {
      const ok = /\.(jpe?g|png|gif|webp|heic|heif|svg|tiff?|pdf|ai|eps)$/i;
      const problems = [];
      [...fileList].forEach((f) => {
        if (!ok.test(f.name)) problems.push(this.msg.msgBadType + ' ' + f.name);
        else if (f.size > 20 * 1024 * 1024) problems.push(this.msg.msgTooBig + ' ' + f.name);
        else if (this.files.length + this.done.length >= 4) problems.push(this.msg.msgTooMany);
        else if (!this.files.concat(this.done).some((x) => x.name === f.name && x.size === f.size)) this.files.push(f);
      });
      this.showError([...new Set(problems)].join(' '));
      this.renderFiles();
    }
    size(bytes) {
      return bytes < 1048576 ? Math.max(1, Math.round(bytes / 1024)) + ' kB' : (bytes / 1048576).toFixed(1).replace('.', ',') + ' MB';
    }
    renderFiles() {
      const row = (f, i, done) => {
        const li = document.createElement('li');
        if (done) li.className = 'is-done';
        const name = document.createElement('span');
        name.textContent = (done ? '✓ ' : '') + f.name + ' · ' + this.size(f.size);
        li.append(name);
        if (!done) {
          const rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'lead-files__remove';
          rm.textContent = '×';
          rm.setAttribute('aria-label', this.msg.msgRemove + ' ' + f.name);
          rm.addEventListener('click', () => {
            if (this.busy) this.cancelUpload(); // × during an upload stops it and drops that file
            this.files.splice(i, 1); this.showError(''); this.renderFiles();
          });
          li.append(rm);
        }
        return li;
      };
      this.list.replaceChildren(...this.done.map((f, i) => row(f, i, true)), ...this.files.map((f, i) => row(f, i, false)));
      this.nextBtn.textContent = this.files.length || this.done.length ? this.msg.msgNext : this.msg.msgNextWithout;
      this.withoutBtn.hidden = true;
    }
    showError(text) {
      this.error.textContent = text;
      this.error.hidden = !text;
    }
    setNote(text) {
      this.note.disabled = false;
      this.note.value = text;
    }
    filesNext() {
      if (this.busy) return;
      const body = this.form.querySelector('textarea[name="contact[body]"]');
      if (body && !body.checkValidity()) { body.reportValidity(); return; }
      if (this.files.length) this.upload();
      else this.go(this.current + 1);
    }
    setBusy(on) {
      this.busy = on;
      this.box.classList.toggle('is-busy', on);
      this.nextBtn.disabled = on;
      this.querySelectorAll('[data-prev]').forEach((b) => { b.disabled = on; });
      this.input.disabled = on;
    }
    async upload() {
      const total = this.files.reduce((s, f) => s + f.size, 0);
      if (total > 30 * 1024 * 1024) { this.showError(this.msg.msgTotalTooBig); return; }
      const typ = this.form.querySelector('input[name="contact[Typ]"]:checked');
      const text = (this.form.querySelector('textarea[name="contact[body]"]') || {}).value || '';
      const body = [
        'Soubory k poptávce z webu – kód ' + this.code + ' (stránka ' + location.pathname + ')',
        'Typ: ' + (typ ? typ.value : '—'),
        '',
        text,
        '',
        'Jméno a kontakt přijdou v poptávce z webu se stejným kódem ' + this.code + '.'
      ].join('\n');
      this.setBusy(true);
      this.showError('');
      this.withoutBtn.hidden = true;
      // fetch has no upload progress for this endpoint, so the bar follows an estimate and jumps to 100 % at the end
      const label = this.files.length + ' · ' + this.size(total);
      const estimate = 4000 + (total / 1048576) * 9000; // measured 03.10: ~75 s for 7.5 MB through Apps Script
      const start = Date.now();
      const tick = () => {
        const p = Math.min(95, Math.round(95 * (1 - Math.exp(-2.2 * (Date.now() - start) / estimate))));
        this.bar.style.width = p + '%';
        this.status.textContent = this.msg.msgUploading + ' ' + label + ' – ' + p + ' %. ' + this.msg.msgWait;
      };
      this.bar.style.width = '0%';
      this.progressBox.hidden = false;
      tick();
      const timer = setInterval(tick, 250);
      this.abort = new AbortController();
      const signal = this.abort.signal;
      try {
        const b64 = (f) => new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(',')[1] || '');
          r.onerror = rej;
          r.readAsDataURL(f);
        });
        const files = await Promise.all(this.files.map(async (f) => ({ name: f.name, data: await b64(f) })));
        if (signal.aborted) throw new DOMException('cancelled', 'AbortError');
        const res = await fetch(this.box.dataset.upload, {
          method: 'POST', signal,
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({
            subject: 'Soubory k poptávce – kód ' + this.code,
            silent: true, // no e-mail now — the shop gets one e-mail from the Shopify form at the last step
            code: this.code, body, files,
            website: this.box.querySelector('[data-hp]').value
          })
        });
        const out = await res.json();
        if (!out.ok) throw new Error(out.error || 'upload');
        clearInterval(timer);
        this.bar.style.width = '100%';
        this.status.textContent = this.msg.msgDone;
        this.done = this.done.concat(this.files);
        this.files = [];
        if (out.folder) this.folder = out.folder;
        this.setNote(this.done.length + ' – ' + this.done.map((f) => f.name).join(', ')
          + (this.folder ? ' · složka na Google Drive: ' + this.folder : '')
          + ' (kód ' + this.code + ')' + (out.refused ? ' · odmítnuto ' + out.refused : ''));
        this.setBusy(false);
        this.renderFiles();
        setTimeout(() => { this.progressBox.hidden = true; this.go(this.current + 1); }, 700);
      } catch (err) {
        clearInterval(timer);
        this.progressBox.hidden = true;
        this.setBusy(false);
        if (err && err.name === 'AbortError') {
          this.newCode(); // a cancelled request may still reach Drive — start the next one under a new code
          this.showError(this.msg.msgCancelled);
          this.renderFiles();
          return;
        }
        this.showError(this.msg.msgFailed);
        this.withoutBtn.hidden = false;
      }
    }
    preselect(value) {
      if (!value) return;
      const radio = [...this.querySelectorAll('input[type="radio"][name="contact[Typ]"]')]
        .find((r) => r.value.toLowerCase() === String(value).toLowerCase());
      if (radio) radio.checked = true;
    }
    go(index) {
      if (index < 0 || index >= this.steps.length) return;
      if (index > this.current) {
        const invalid = [...this.steps[this.current].querySelectorAll('input, textarea')].find((f) => !f.checkValidity());
        if (invalid) { invalid.reportValidity(); return; }
      }
      this.current = index;
      this.steps.forEach((s, i) => s.classList.toggle('is-current', i === index));
      this.progress.forEach((p, i) => p.classList.toggle('is-done', i <= index));
      const focusTarget = this.steps[index].querySelector('input, textarea, button');
      if (focusTarget && index > 0) focusTarget.focus({ preventScroll: false });
    }
  }
  customElements.define('lead-form', LeadForm);

  /* Sticky mobile CTA — appears after the first screen, hides at the form */
  const initMobileCta = () => {
    const bar = document.querySelector('[data-mobile-cta]');
    if (!bar) return;
    const target = document.querySelector('#poptavka');
    let formVisible = false;
    if (target && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { formVisible = entry.isIntersecting; update(); }).observe(target);
    }
    const update = () => bar.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.6 && !formVisible);
    window.addEventListener('scroll', update, { passive: true });
    update();
  };

  const init = () => { initReveal(); initMobileCta(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Theme editor: re-run when a section is added or changed */
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target.closest('[role="tab"]') || e.target.querySelector?.('[role="tab"]');
    const tabs = e.target.closest('tabs-block');
    if (tabs && tab) tabs.show(tabs.tabs.indexOf(tab));
    const typeBtn = e.target.matches?.('[data-type-index]') ? e.target : null;
    const hero = e.target.closest('hero-orientation');
    if (hero && typeBtn) hero.select(Number(typeBtn.dataset.typeIndex));
  });
})();

// Mobile menu: open the panel exactly below the header
document.querySelectorAll('.menu-drawer').forEach((drawer) => {
  drawer.addEventListener('toggle', () => {
    if (!drawer.open) return;
    const header = drawer.closest('.header');
    if (header) drawer.style.setProperty('--drawer-top', Math.max(0, header.getBoundingClientRect().bottom) + 'px');
  });
});

/* Language switcher: close the dropdown on outside click or Escape */
document.addEventListener('click', (e) => {
  document.querySelectorAll('details.lang[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') document.querySelectorAll('details.lang[open]').forEach((d) => d.removeAttribute('open'));
});
